import React, { useState, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import { Insight, insightsService } from '../../services/insightsService';
import { getTypeMeta, timeAgo, REVIEW_STATUS_META } from './insightPresentation';

interface RecentInsightsProps {
  pageSize?: number;
}

const RecentInsights: React.FC<RecentInsightsProps> = ({ pageSize = 10 }) => {
  const navigate = useNavigate();
  const [insights, setInsights] = useState<Insight[]>([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);
  const [page, setPage] = useState(1);
  const [totalPages, setTotalPages] = useState(1);

  useEffect(() => {
    const fetchRecent = async () => {
      try {
        setLoading(true);
        setError(null);
        const res = await insightsService.getInsights({
          review_status: 'all',
          perpage: pageSize,
          page,
        });
        setInsights(res.results);
        setTotalPages(Math.max(1, res.totalPages));
      } catch (err) {
        console.error('Failed to fetch recent insights:', err);
        setError('No se pudieron cargar los insights.');
      } finally {
        setLoading(false);
      }
    };
    fetchRecent();
  }, [page, pageSize]);

  const pageInsights = insights;

  return (
    <div
      style={{
        background: '#fff',
        borderRadius: 12,
        padding: 24,
        boxShadow: '0 1px 3px rgba(0,0,0,0.1)',
        marginBottom: 32,
      }}
    >
      <h2 style={{ fontSize: 20, fontWeight: 700, color: '#1f2937', marginTop: 0, marginBottom: 20 }}>
        Insights recientes
      </h2>

      {loading ? (
        <div style={{ padding: 40, fontSize: 16, color: '#6b7280', textAlign: 'center' }}>
          Cargando insights...
        </div>
      ) : error ? (
        <div style={{ padding: 40, fontSize: 16, color: '#e53e3e', textAlign: 'center' }}>{error}</div>
      ) : insights.length === 0 ? (
        <div style={{ padding: 40, fontSize: 16, color: '#6b7280', textAlign: 'center' }}>
          No hay insights recientes
        </div>
      ) : (
        <div style={{ display: 'flex', flexDirection: 'column', gap: 10 }}>
          {pageInsights.map((insight) => {
            const typeMeta = getTypeMeta(insight.type);
            const name = insight.patient.name;
            return (
              <div
                key={insight.id}
                onClick={() => navigate(`/app/patients/${insight.patient.patient_id}?tab=insights`)}
                style={{
                  display: 'flex',
                  alignItems: 'center',
                  gap: 12,
                  padding: 14,
                  borderRadius: 8,
                  border: '1px solid #f3f4f6',
                  borderLeft: `4px solid ${typeMeta.color}`,
                  cursor: 'pointer',
                  transition: 'all 0.2s',
                }}
                onMouseEnter={(e) => {
                  e.currentTarget.style.background = '#f9fafb';
                  e.currentTarget.style.borderColor = '#e5e7eb';
                  e.currentTarget.style.borderLeftColor = typeMeta.color;
                }}
                onMouseLeave={(e) => {
                  e.currentTarget.style.background = 'transparent';
                  e.currentTarget.style.borderColor = '#f3f4f6';
                  e.currentTarget.style.borderLeftColor = typeMeta.color;
                }}
              >
                <span style={{ fontSize: 18, flexShrink: 0 }}>{typeMeta.emoji}</span>
                <div style={{ flex: 1, minWidth: 0 }}>
                  <div
                    style={{
                      color: '#1f2937',
                      fontSize: 14,
                      fontWeight: 500,
                      lineHeight: 1.4,
                    }}
                  >
                    {insight.summary}
                  </div>
                  <div style={{ color: '#6b7280', fontSize: 13 }}>
                    {name} · {timeAgo(insight.created_at)}
                  </div>
                </div>
                {(() => {
                  const statusMeta = REVIEW_STATUS_META[insight.review_status];
                  return (
                    <span
                      style={{
                        background: statusMeta.bg,
                        color: statusMeta.color,
                        fontSize: 11,
                        fontWeight: 600,
                        padding: '3px 8px',
                        borderRadius: 10,
                        whiteSpace: 'nowrap',
                      }}
                    >
                      {statusMeta.label}
                    </span>
                  );
                })()}
                <div style={{ color: '#6b7280', fontSize: 20 }}>→</div>
              </div>
            );
          })}
        </div>
      )}

      {!loading && !error && totalPages > 1 && (
        <div
          style={{
            display: 'flex',
            alignItems: 'center',
            justifyContent: 'center',
            gap: 8,
            flexWrap: 'wrap',
            marginTop: 20,
          }}
        >
          <button
            onClick={() => setPage((p) => Math.max(1, p - 1))}
            disabled={page <= 1}
            style={{
              background: 'transparent',
              color: page <= 1 ? '#cbd5e1' : '#38b2ac',
              border: `1px solid ${page <= 1 ? '#e5e7eb' : '#38b2ac'}`,
              borderRadius: 8,
              padding: '8px 12px',
              fontSize: 14,
              fontWeight: 600,
              cursor: page <= 1 ? 'not-allowed' : 'pointer',
              transition: 'all 0.2s',
            }}
          >
            ←
          </button>
          {Array.from({ length: totalPages }, (_, i) => i + 1).map((p) => {
            const active = p === page;
            return (
              <button
                key={p}
                onClick={() => setPage(p)}
                style={{
                  background: active ? '#38b2ac' : 'transparent',
                  color: active ? '#fff' : '#38b2ac',
                  border: `1px solid ${active ? '#38b2ac' : '#e5e7eb'}`,
                  borderRadius: 8,
                  minWidth: 36,
                  padding: '8px 10px',
                  fontSize: 14,
                  fontWeight: 600,
                  cursor: active ? 'default' : 'pointer',
                  transition: 'all 0.2s',
                }}
              >
                {p}
              </button>
            );
          })}
          <button
            onClick={() => setPage((p) => Math.min(totalPages, p + 1))}
            disabled={page >= totalPages}
            style={{
              background: 'transparent',
              color: page >= totalPages ? '#cbd5e1' : '#38b2ac',
              border: `1px solid ${page >= totalPages ? '#e5e7eb' : '#38b2ac'}`,
              borderRadius: 8,
              padding: '8px 12px',
              fontSize: 14,
              fontWeight: 600,
              cursor: page >= totalPages ? 'not-allowed' : 'pointer',
              transition: 'all 0.2s',
            }}
          >
            →
          </button>
        </div>
      )}
    </div>
  );
};

export default RecentInsights;
